import type { ThemeDefinition, ThemeMode } from './types';
import { getTheme } from './registry';

/**
 * The one place a theme is turned into pixels.
 *
 * Writes the selected theme's tokens onto <html> as inline custom properties,
 * and flips the `light`/`dark` class the Tailwind `darkMode: 'class'` strategy
 * and every `.dark` CSS rule already key off. Nothing else in the app writes a
 * `--co-*` value, which is what makes "add a theme" a data change.
 *
 * Inline properties (rather than a generated stylesheet) are deliberate: they
 * win over any rule without a specificity contest, they are trivially
 * removable when a theme that omits a token is selected next, and they are
 * visible in devtools next to the element that uses them.
 */

/** Tailwind ramps arrive as `--co-tw-<family>-<shade>`. */
const TW_PREFIX = '--co-tw-';

/**
 * Keys written by the previous call.
 *
 * Switching themes has to REMOVE what the old theme set, not just overwrite
 * it: a theme that declares fewer tokens than its predecessor would otherwise
 * inherit the leftovers and paint a hybrid.
 */
let appliedKeys: string[] = [];

/** `#2A62C4` -> `42 98 196`, the channel form Tailwind's `<alpha-value>` needs. */
function toChannels(hex: string): string {
  const h = hex.trim().replace('#', '');
  const full =
    h.length === 3
      ? h
          .split('')
          .map((c) => c + c)
          .join('')
      : h;
  const n = Number.parseInt(full.slice(0, 6), 16);
  if (!Number.isFinite(n)) return '0 0 0';
  // eslint-disable-next-line no-bitwise
  return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`;
}

/** Every property a theme + mode pair resolves to, as a flat map. */
export function resolveTokens(
  theme: ThemeDefinition,
  mode: ThemeMode,
): Record<string, string> {
  const out: Record<string, string> = {
    ...theme.base,
    ...(mode === 'dark' ? theme.dark : theme.light),
  };

  const palette = theme.palette as unknown as Record<string, Record<string, string>>;
  for (const [family, ramp] of Object.entries(palette)) {
    for (const [shade, hex] of Object.entries(ramp)) {
      out[`${TW_PREFIX}${family}-${shade}`] = toChannels(hex);
    }
  }

  return out;
}

/**
 * Apply an application theme and an appearance together.
 *
 * Both arguments are required because they are not independent at paint time:
 * the semantic tokens are per-mode, so flipping light/dark has to rewrite them
 * and not only swap a class.
 */
export function applyTheme(themeId: string, mode: ThemeMode): void {
  if (typeof document === 'undefined') return;

  const theme = getTheme(themeId);
  const tokens = resolveTokens(theme, mode);
  const root = document.documentElement;

  for (const key of appliedKeys) {
    if (!(key in tokens)) root.style.removeProperty(key);
  }

  for (const [key, value] of Object.entries(tokens)) {
    root.style.setProperty(key, value);
  }
  appliedKeys = Object.keys(tokens);

  root.classList.remove('light', 'dark');
  root.classList.add(mode);
  root.setAttribute('data-theme', mode);
  // Lets CSS and anything watching the document target a specific theme
  // without reading the store, and gives charts a single attribute to observe.
  root.setAttribute('data-app-theme', theme.id);
}
